import React, { useEffect, useState, useRef } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  InputLabel,
} from '@mui/material';

const sizeOverrides = {
  'extra-small': '219px',
  small: '301px',
  medium: '403px',
  large: '534px',
  half: '500px',
  full: '1000px',
  'vehicle-return': '324px',
  'vehicle-return-full': '662px',
  'vehicle-full': '448px',
  'accounts-half': '416px',
  'accounts-full': '864px',
};

const AsyncDropdownWithExtraOptionComponent = ({
  name,
  question,
  value,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  inputRef,
  nextFieldRef,
  isMobile,
  layoutVariant = 'default',
}) => {
  const isAccountLayout = layoutVariant === 'account';
  const [options, setOptions] = useState([]);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const fieldRef = inputRef || useRef(null);

  const {
    customSize,
    placeholder,
    label,
    validation,
    apiEndpoint,
    extraOptionLabel = '+ Add Another',
    fallbackOptions = [],
  } = question;

  const fieldWidth = customSize ? sizeOverrides[customSize] || '100%' : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;
  const fieldHeight = isMobile ? '51px' : '53px';
  const fontSize = '1rem';

  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const validate = (input = valueRef.current, showError = false) => {
    const isValid = !(
      validation?.required &&
      (!input || input === extraOptionLabel)
    );
    if (touched || showError) {
      setError(isValid ? '' : validation?.message || 'Required');
    }
    setValidationState?.(name, isValid);
    return isValid;
  };

  useEffect(() => {
    registerValidation?.(name, () => validate(undefined, true));

    const fetchOptions = async () => {
      try {
        const response = await fetch(apiEndpoint);
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        setOptions([...list, extraOptionLabel]);
        setTimeout(() => validate(), 0);
      } catch (err) {
        setOptions([...fallbackOptions, extraOptionLabel]);
        setTimeout(() => validate(), 0);
      }
    };

    if (apiEndpoint) {
      fetchOptions();
    } else {
      setOptions([...fallbackOptions, extraOptionLabel]);
      setTimeout(() => validate(), 0);
    }

    setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const selected = e.target.value;

    if (selected === extraOptionLabel) {
      return;
    }

    setTouched(true);
    onChange(name, selected);

    setTimeout(() => {
      if (validate(selected)) {
        nextFieldRef?.current?.focus() ?? onNext?.();
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setTouched(true);
      setTimeout(() => {
        if (validate()) nextFieldRef?.current?.focus() ?? onNext?.();
      }, 0);
    }
  };

  const hasValue = value !== undefined && value !== null && value !== '';
  const labelId = `${name}-label`;

  return (
    <FormControl
      fullWidth
      error={!!error}
      sx={{
        width: responsiveWidth,
        mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        maxWidth: '100%',
        zIndex: 2001, // match VehicleSearch stacking
        '& .MuiOutlinedInput-root': {
          height: fieldHeight,
          minHeight: fieldHeight,
          borderRadius: '12px',
          padding: '0 12px',
          fontFamily: 'Inter, sans-serif',
          fontSize,
          letterSpacing: '-0.02em',
          backgroundColor: '#fff',
          color: hasValue ? '#162F3B' : '#738289',
          '& fieldset': {
            borderRadius: '12px',
            borderColor: '#A2ACB1', // default border color (matches vehicle)
            borderWidth: '1px',
          },
        },
        '& .MuiSelect-select': {
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'Inter, sans-serif',
          fontSize,
          letterSpacing: '-0.02em',
          color: hasValue ? '#162F3B' : '#738289', // value vs placeholder
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .MuiSelect-icon': {
          color: '#162F3B',
          fontSize: 24,
        },
        '& .MuiFormHelperText-root': {
          textAlign: 'right',
          fontFamily: 'Inter, sans-serif',
          fontSize: isMobile ? '0.6rem' : '0.75rem',
          color: '#D32F2F',
          mt: '4px',
          pr: '8px',
        },
      }}
    >
      {/* Only render the label when there is a value (to match VehicleSearch behavior) */}
      {hasValue && (
        <InputLabel
          id={labelId}
          shrink
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            letterSpacing: '-0.02em',
            color: '#738289',
          }}
        >
          {label}
        </InputLabel>
      )}

      <Select
        value={value ?? ''} // keep controlled
        onChange={handleChange}
        displayEmpty
        inputRef={fieldRef}
        onKeyDown={handleKeyDown}
        variant="outlined"
        labelId={hasValue ? labelId : undefined} // tie to InputLabel only when it exists
        label={hasValue ? label : undefined} // ensures proper notch + shrink
        MenuProps={{
          disablePortal: false,
          PaperProps: {
            sx: {
              mt: '10px',
              width: responsiveWidth,
              maxHeight: `calc(${fieldHeight} * 6)`,
              overflowY: 'auto',
              border: '1px solid #A2ACB1',
              borderRadius: '20px',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              zIndex: 2002,
            },
          },
        }}
        renderValue={(selected) =>
          selected ? (
            selected
          ) : (
            <Typography
              sx={{
                color: '#738289', // placeholder color
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize,
                lineHeight: '1.2rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {placeholder || label || ''}
            </Typography>
          )
        }
      >
        {options.map((option, idx) => (
          <MenuItem
            key={idx}
            value={option}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize,
              color: '#162F3B',
              height: fieldHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              px: '28px',
              '&.Mui-selected': { backgroundColor: 'transparent !important' },
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Select>

      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default AsyncDropdownWithExtraOptionComponent;
